"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { PaymentCard } from '@/components/payments/PaymentCard'
import { PaymentElement } from '@stripe/react-stripe-js'

import { StripeConnectButton } from "@/components/StripeConnectButton";
import { useFunnelOwnerId } from "@/hooks/useFunnelOwnerId";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useSellerId } from "@/hooks/useSellerId";
import { useScopedTranslations } from "@/lib/i18n/useScopedTranslations";

import { CryptoCheckoutPanel } from "@/components/crypto/CryptoCheckoutPanel";
import { TransakCheckoutPanel } from '@/components/checkout/TransakCheckoutPanel'
import { CompleteAccountGatedActionButton } from "@/components/auth/CompleteAccountGatedActionButton";
import { useIsAdmin } from '@/hooks/useIsAdmin'

export type Payee = 'seller' | 'platform'

export type PlatformMeta = {
  enabled: boolean
  kind?:
    | 'storage'
    | 'membership'
    | 'addon'
    | 'credits'
    | 'emails'
    | 'community_access'
    | 'other'
  plan_key?: string
  pack_key?: string
  tier_key?: string
  group_id?: number
  seller_id?: number
  access_mode?: 'one_time' | 'recurring'
}

interface CheckoutFormBlockProps {
  data: {
    enableInstallments?: boolean;
    bumpProductId?: string;
    bumpProductName?: string;
    bumpProductPrice?: number;
    bumpProductDescription?: string;
    bumpProductImage?: string;

    amount?: number;
    amountUSD?: number;
    quantity?: number;
    unitPrice?: number;
    unitPriceUSD?: number;
    totalPrice?: number;
    totalPriceUSD?: number;
    currency?: string;
    productId?: number;
    funnelPageId?: number;
    productName?: string;
    productDescription?: string;

    // OJO: recurringPlan te llega desde tu funnel/page data
    recurringPlan?: any;

    allowManualCryptoForRecurring?: boolean;

    thankyou?: {
      title?: string;
      message?: string;
      video?: string;
      action?:
        | 'home'
        | 'onboarding'
        | 'moments'
        | 'community'
        | 'order'
        | 'membership'
        | 'link'
      link?: string;
      button?: string;
      status?: number | boolean
    };

    paymentMethods?: {
      hasStripe: boolean;
      hasCrypto: boolean;
      hasTransak?: boolean

      cryptoWallet?: string | null;
      cryptoChain?: string;
      cryptoToken?: string;
      
      transakStatus?: string | null
      transakEnvironment?: string | null
    };
    payee?: Payee
    platform?: PlatformMeta

    platformFee?: number;
    markup?: number;
    markupWallet?: string | null;

    // si ya lo metes desde arriba, lo respetamos
    sellerId?: number | string;
    userId?: number | string;
    aiAgentId?: number | null;
  }
  isEditing?: boolean;
  onChange?: (data: any) => void;
  onSuccess?: () => void;
}

function getMxCheckoutKey({
  sellerId,
  productId,
  funnelPageId,
  currency,
  amount,
  quantity = 1,
  platform,
  }: {
  sellerId?: string | number | null
  productId?: number | null
  funnelPageId?: number | null
  currency?: string | null
  amount?: number | null
  quantity?: number | null
  platform?: any
}) {
  return [
    'mx_pi',
    sellerId ?? 'none',
    productId ?? 'none',
    funnelPageId ?? 'none',
    String(currency || '').toUpperCase(),
    Number(amount || 0).toFixed(2),
    String(quantity || 1),
    platform?.enabled ? String(platform.kind || 'platform') : 'no-platform',
    platform?.pack_key || '',
    platform?.plan_key || '',
    platform?.group_id || '',
  ].join('_')
}

function getRecurringCheckoutDescription(
  plan: any,
  currency: string,
  formatMoney: (amount: number, currency: string) => string
) {
  const freq =
    Number(plan.cycle) === 1
      ? plan.frequency
      : `${plan.cycle} ${plan.frequency}s`

  const normalPriceFormatted = formatMoney(Number(plan.price || 0), currency)

  const hasIntroPrice =
    Number(plan?.intro_enabled || 0) === 1 &&
    Number(plan?.intro_price || 0) > 0 &&
    Number(plan?.intro_duration_months || 0) > 0

  if (hasIntroPrice) {
    return `${formatMoney(Number(plan.intro_price), currency)} cada ${freq} durante los primeros ${Number(plan.intro_duration_months)} meses. Después ${normalPriceFormatted} cada ${freq}. Cancela cuando quieras.`
  }

  if (plan.trial?.enabled && plan.trial.cycle > 0) {
    const trialFreq =
      plan.trial.cycle === 1
        ? plan.trial.frequency
        : `${plan.trial.cycle} ${plan.trial.frequency}s`

    return `Gratis durante ${trialFreq}. Luego ${normalPriceFormatted} cada ${freq}. Cancela cuando quieras.`
  }

  return `${normalPriceFormatted} cada ${freq}. Cancela cuando quieras.`
}


function toEmbedUrl(url?: string) {
  if (!url) return undefined;

  try {
    const u = new URL(url);

    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }

    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed${u.pathname}`;
    }

    if (u.hostname.includes("vimeo.com")) {
      const videoId = u.pathname.split("/").filter(Boolean)[0];
      if (videoId) return `https://player.vimeo.com/video/${videoId}`;
    }

    return url;
  } catch {
    return undefined;
  }
}

function CheckoutFormInner({
  data,
  bumpSelected,
  setBumpSelected,
}: {
  data: any;
  bumpSelected: boolean;
  setBumpSelected: (val: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const {
    isAdmin,
    loading: adminLoading,
  } = useIsAdmin()

  const sellerId = data.sellerId;
  const userId = data.userId;

  const hasStripe = !!data.paymentMethods?.hasStripe;
  const hasCrypto = !!data.paymentMethods?.hasCrypto;
  const recurring = data.recurringPlan;

  const isRecurring = !!recurring;

  const allowRecurringCrypto =
    Boolean(
      isRecurring &&
      data.allowManualCryptoForRecurring
    )

  const cryptoAvailable =
    Boolean(
      hasCrypto &&
      (
        !isRecurring ||
        allowRecurringCrypto
      )
    )
    
  const currency = data.currency;

  const quantity = isRecurring
    ? 1
    : Math.max(1, Math.min(99, Math.floor(Number(data.quantity || 1))))

  const unitPrice = Number.isFinite(Number(data.unitPrice))
    ? Number(data.unitPrice)
    : Number(data.amount || 99.99)

  const unitPriceUSD = Number.isFinite(Number(data.unitPriceUSD))
    ? Number(data.unitPriceUSD)
    : Number(data.amountUSD || 99.99)

  const amountPlan = Number.isFinite(Number(data.amount))
    ? Number(data.amount)
    : unitPrice * quantity

  const amountPlanUSD = Number.isFinite(Number(data.amountUSD))
    ? Number(data.amountUSD)
    : unitPriceUSD * quantity

  const [recurringPreview, setRecurringPreview] = useState<null | {
    due_now: number
    recurring: number
    label: string
    upfront_months?: number
  }>(null)

  useEffect(() => {
    let alive = true

    async function run() {
      try {
        setRecurringPreview(null)

        // solo aplica a recurring + platform membership
        if (!isRecurring) return
        if (!data?.platform?.enabled) return
        if (data.platform.kind !== 'membership') return

        const res = await fetch('/api/stripe/preview-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            currency: data.currency,
            recurring: data.recurringPlan,
            platform: data.platform,
          }),
        })

        const j = await res.json()
        //console.log('MX PRECREATE INTENT RESPONSE', j)
        if (!res.ok) throw new Error(j?.error || 'Preview error')

        if (alive) {
          setRecurringPreview({
            due_now: Number(j?.due_now || 0),
            recurring: Number(j?.recurring || 0),
            label: String(j?.label || ''),
            upfront_months: Number(j?.upfront_months || 0),
          })
        }
      } catch (err) {
        // si falla preview, no rompemos checkout (solo cae al comportamiento normal)
        console.error('preview-subscription error:', err)
        if (alive) setRecurringPreview(null)
      }
    }

    run()
    return () => {
      alive = false
    }
  }, [isRecurring, data?.platform?.enabled, data?.platform?.kind, data?.platform?.group_id, userId, data?.currency, data?.recurringPlan?.price])

  useEffect(() => {

    console.log("CHECKOUT DATA DEBUG", {
      data,
      thankyou: data?.thankyou,
      thankyouTitle: data?.thankyou?.title,
      thankyouMessage: data?.thankyou?.message,
      thankyouVideo: data?.thankyou?.video,
      thankyouAction: data?.thankyou?.action,
    });
  }, [data]);

  const formatMoney = (amount: number, currency: string) => {
    return new Intl.NumberFormat(
      currency === "MXN" ? "es-MX" : "en-US",
      {
        style: "currency",
        currency,
      }
    ).format(amount);
  };

  const amountToday = useMemo(() => {
    if (!recurring) return amountPlan

    // ✅ si tenemos preview, manda el "due_now" (ej 94)
    if (recurringPreview?.due_now && recurringPreview.due_now > 0) {
      return recurringPreview.due_now
    }

    // fallback legacy
    if (recurring.trial?.enabled && recurring.trial.cycle > 0) {
      return recurring.trial.price ?? 0
    }

    if (
      Number(recurring?.intro_enabled || 0) === 1 &&
      Number(recurring?.intro_price || 0) > 0 &&
      Number(recurring?.intro_duration_months || 0) > 0
    ) {
      return Number(recurring.intro_price)
    }

    return recurring.price
  }, [recurring, amountPlan, recurringPreview])

  const amountTodayUSD = useMemo(() => {
    if (!recurring) return amountPlanUSD;
    if (recurring.trial?.enabled && recurring.trial.cycle > 0) {
      return recurring.trial.price ?? 0;
    }

    if (
      Number(recurring?.intro_enabled || 0) === 1 &&
      Number(recurring?.intro_price || 0) > 0 &&
      Number(recurring?.intro_duration_months || 0) > 0
    ) {
      return Number(recurring.intro_price)
    }

    return recurring.price;
  }, [recurring, amountPlanUSD]);

  const hasBumpOffer = data.bumpProductId && data.bumpProductId !== "none";

  const bumpOffer = useMemo(() => {
    if (!hasBumpOffer || !bumpSelected) return undefined;

    return {
      name: data.bumpProductName || "Producto Adicional",
      price: data.bumpProductPrice || 29.99,
      description: data.bumpProductDescription,
      image: data.bumpProductImage,
    };
  }, [
    hasBumpOffer,
    bumpSelected,
    data.bumpProductName,
    data.bumpProductPrice,
    data.bumpProductDescription,
    data.bumpProductImage,
  ]);

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const [couponCode, setCouponCode] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState<string | null>(null)
  const [appliedCoupon, setAppliedCoupon] = useState<any | null>(null)

  const totalAmount = useMemo(() => {
    return bumpOffer ? amountToday + bumpOffer.price : amountToday;
  }, [amountToday, bumpOffer]);

  const totalAmountUSD = useMemo(() => {
    return bumpOffer ? amountTodayUSD + bumpOffer.price : amountTodayUSD;
  }, [amountTodayUSD, bumpOffer]);

  const isEnergyCheckout =
    data.platform?.enabled === true &&
    data.platform?.kind === 'credits'

  const discountAmount = isEnergyCheckout
    ? 0
    : Number(appliedCoupon?.discountAmount || 0)

  const payableAmount = useMemo(() => {
    return Math.max(0, Number(totalAmount || 0) - discountAmount)
  }, [totalAmount, discountAmount])

  const payableAmountUSD = useMemo(() => {
    return Math.max(0, Number(totalAmountUSD || 0) - discountAmount)
  }, [totalAmountUSD, discountAmount])

  const hasIntroPrice =
    isRecurring &&
    Number(recurring?.intro_enabled || 0) === 1 &&
    Number(recurring?.intro_price || 0) > 0 &&
    Number(recurring?.intro_duration_months || 0) > 0

  const recurringDisplayPrice = hasIntroPrice
    ? Number(recurring.intro_price)
    : Number(recurring?.price || 0)

  const recurringNormalPrice = Number(recurring?.price || 0)

  const introDurationMonths = Number(recurring?.intro_duration_months || 0)

  const { user, email: userEmail, name: userFullName } = useCurrentUser();

  const { t, isLoading: i18nLoading } = useScopedTranslations("product/product");

  type CheckoutPaymentMethod = 'card' | 'crypto' | 'transak'

  const hasTransak = !!data.paymentMethods?.hasTransak && !isRecurring

  function getDefaultPaymentMethod():
    CheckoutPaymentMethod {
    if (
      hasStripe
    ) {
      return 'card'
    }

    if (
      hasTransak
    ) {
      return 'transak'
    }

    if (
      cryptoAvailable
    ) {
      return 'crypto'
    }

    return 'card'
  }

  const [method, setMethod] = useState<CheckoutPaymentMethod>(
    getDefaultPaymentMethod()
  )

  const completeCheckoutSuccess = (
    message = '¡Pago procesado exitosamente!'
  ) => {
    setPaymentSuccess(true)

    toast.success(
      message
    )

    /*
    * ESTE es el mismo success event
    * para pago real y simulación ADMIN.
    */
    setTimeout(
      () => {
        data.onSuccess?.()
      },
      0
    )
  }

  useEffect(() => {
    if (
      isRecurring &&
      !allowRecurringCrypto &&
      method === 'crypto'
    ) {
      setMethod('card')
    }
  }, [
    isRecurring,
    allowRecurringCrypto,
    method,
  ])

  const isMxInstallmentsFlow =
  !!data.isMxInstallmentsCheckout && !isRecurring

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    /*
    * =========================================
    * ADMIN PAYMENT SIMULATION
    * =========================================
    *
    * No Stripe.
    * No PaymentIntent.
    * No order.
    * No purchase.
    * No DB.
    *
    * Solamente dispara exactamente
    * el mismo success del checkout real.
    */
    if (
      isAdmin &&
      !adminLoading
    ) {
      setLoading(true)

      try {
        completeCheckoutSuccess(
          'Simulación ADMIN completada'
        )
      } finally {
        setLoading(false)
      }

      return
    }

    const finalEmail = userEmail || email;
    const finalName = name || userFullName || "";

    if (!stripe || !elements) {
      toast.error("Stripe no está listo todavía");
      return;
    }

    if (!finalEmail || !finalName) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    if (!cardComplete) {
      toast.error("Ingresa una tarjeta válida para continuar");
      return;
    }

    const cardElement = !isMxInstallmentsFlow
      ? elements.getElement(CardElement)
      : null

    if (!isMxInstallmentsFlow && !cardElement) {
      toast.error("Error al cargar el formulario de pago");
      return;
    }

    setLoading(true);

    try {
      const productName = data.productName || "Producto";
      const productDescription = data.productDescription || "";
      const productId = data.productId || 0;
      const funnelPageId = data.funnelPageId || 0;

      const tenantSellerId =
        (data.sellerId ?? sellerId ?? null) // o process.env.NEXT_PUBLIC_PLATFORM_TEST_IDENTIFIER

      // ==============================
      // ✅ RECURRENT (Connect Elements)
      // ==============================
      if (isRecurring) {
        const res = await fetch("/api/stripe/create-subscription-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            seller_id: sellerId,
            user_id: userId,
            ai_agent_id: data.aiAgentId,
            email: finalEmail,
            buyer_name: finalName,
            currency: data.currency,
            product: { id: productId, name: productName },
            recurring: {
              recurring_id: recurring.recurring_id,
              price: recurring.price,
              frequency: recurring.frequency,
              cycle: recurring.cycle,
              intro_enabled: recurring.intro_enabled,
              intro_price: recurring.intro_price,
              intro_duration_months: recurring.intro_duration_months,
              trial: recurring.trial
                ? {
                    enabled: !!recurring.trial.enabled,
                    price: recurring.trial.price,
                    frequency: recurring.trial.frequency,
                    cycle: recurring.trial.cycle,
                    duration: recurring.trial.duration,
                  }
                : { enabled: false, price: 0, frequency: "day", cycle: 0 },
            },
            coupon: appliedCoupon
              ? {
                  couponId: appliedCoupon.couponId,
                  code: appliedCoupon.code,
                }
              : null,
            platform: data.platform?.enabled
            ? {
                enabled: true,
                kind: data.platform.kind,

                identifier: tenantSellerId,

                // ✅ storage
                ...(data.platform.kind === 'storage'
                  ? { plan_key: data.platform.plan_key }
                  : {}),

                // ✅ emails
                ...(data.platform.kind === 'emails'
                  ? { tier_key: data.platform.tier_key }
                  : {}),

                // ✅ membership
                ...(data.platform.kind === 'membership'
                  ? { group_id: data.platform.group_id }
                  : {}),

                ...(data.platform.kind === 'community_access'
                  ? {
                      seller_id: data.platform.seller_id,
                      access_mode: data.platform.access_mode,
                    }
                  : {}),
              }
            : undefined,
          }),
        });

        const subData = await res.json();
        if (!res.ok) throw new Error(subData.error || "Error creando suscripción");

        const confirmation = subData.confirmation as
          | "setup_intent"
          | "payment_intent"
          | "none";

        if (confirmation === "setup_intent") {
          const { error } = await stripe.confirmCardSetup(subData.client_secret, {
            payment_method: {
              card: cardElement,
              billing_details: { name: finalName, email: finalEmail },
            },
          });
          if (error) throw new Error(error.message);
        } else if (confirmation === "payment_intent") {
          const { error } = await stripe.confirmCardPayment(subData.client_secret, {
            payment_method: {
              card: cardElement,
              billing_details: { name: finalName, email: finalEmail },
            },
          });
          if (error) throw new Error(error.message);
        }

        completeCheckoutSuccess(
          '¡Suscripción creada!'
        )
        return;
      }

      if (isMxInstallmentsFlow) {
        /*
        console.log('MX CONFIRM DEBUG', {
          isMxInstallmentsFlow,
          cardComplete,
          hasStripe: !!stripe,
          hasElements: !!elements,
        })
        */

        const { error } = await stripe.confirmPayment({
          elements,
          confirmParams: {},
          redirect: 'if_required',
        })
        
        //console.log('MX CONFIRM RESULT', { error })

        if (error) throw new Error(error.message)

        const cacheKey = getMxCheckoutKey({
          sellerId,
          productId: data.productId || 0,
          funnelPageId: data.funnelPageId || 0,
          currency: data.currency,
          amount: data.amount || 0,
          quantity,
          platform: data.platform,
        })

        sessionStorage.removeItem(cacheKey)

        //console.log('✅ MX payment successful with PaymentElement')
        completeCheckoutSuccess()
        return
      }

      // ==============================
      // ✅ SINGLE PAYMENT (Platform Elements)
      // ==============================
      // ✅ resto de pagos: igual que antes
      const res = await fetch("/api/stripe/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: payableAmount,
          currency,
          productName,
          productDescription,
          email: finalEmail,
          buyer_name: finalName,
          seller_id: sellerId,
          user_id: userId,
          product_id: productId,
          ai_agent_id: data.aiAgentId,
          funnel_page_id: funnelPageId,
          quantity,
          unit_price: unitPrice,
          platform: data.platform?.enabled
            ? {
                enabled: true,
                kind: data.platform.kind,
                identifier: tenantSellerId,
                ...(data.platform.kind === 'credits'
                  ? { pack_key: data.platform.pack_key }
                  : {}),
                ...(data.platform.kind === 'storage'
                  ? { plan_key: data.platform.plan_key }
                  : {}),
                ...(data.platform.kind === 'membership'
                  ? { group_id: data.platform.group_id }
                  : {}),
              }
            : undefined,
        }),
      })

      const intentData = await res.json()
      if (!res.ok) throw new Error(intentData.error || "Error creando PaymentIntent")

      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(intentData.clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: { name: finalName, email: finalEmail },
          },
        })

      if (confirmError) throw new Error(confirmError.message)

      //console.log("✅ Payment successful:", paymentIntent?.id);
      completeCheckoutSuccess()
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(error.message || "Error al procesar el pago");
    } finally {
      setLoading(false);
    }
  }

  async function applyCoupon() {
    const code = couponCode.trim().toUpperCase()

    if (!code) {
      setCouponError('Escribe un código de cupón')
      return
    }

    try {
      setCouponLoading(true)
      setCouponError(null)

      const res = await fetch('/api/seller/coupons/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sellerId: data.sellerId || sellerId,
          customerId: userId,
          productId: data.productId || 0,
          recurringId: recurring?.recurring_id || null,
          code,
          mode: isRecurring ? 'subscription' : 'one_time',
          amount: Number(totalAmount || 0),
          currency: data.currency,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'Cupón inválido')
      }

      setAppliedCoupon(json.coupon)
      setCouponCode(code)
      toast.success('Cupón aplicado')
    } catch (err: any) {
      setAppliedCoupon(null)
      setCouponError(err?.message || 'Cupón inválido')
    } finally {
      setCouponLoading(false)
    }
  }

  function removeCoupon() {
    setAppliedCoupon(null)
    setCouponError(null)
    setCouponCode('')
  }



  const availablePaymentMethods = [
    hasStripe
      ? 'card'
      : null,

    cryptoAvailable
      ? 'crypto'
      : null,

    hasTransak
      ? 'transak'
      : null,
  ].filter(
    Boolean
  ) as CheckoutPaymentMethod[]

  const shouldShowPaymentSelector = availablePaymentMethods.length > 1

  if (paymentSuccess) {
    const configuredThankyou = data?.thankyou

    const thankyou =
      Number(configuredThankyou?.status ?? 1) === 1
        ? configuredThankyou
        : null

    const resolvedAction =
      thankyou?.action ||
      (isRecurring ? 'membership' : 'order')

    const internalDestinations: Record<string, string> = {
      home: '/home',
      onboarding: '/onboarding',
      moments: '/moments',
      community: '/community',
      membership: '/settings/memberships',
      order: '/settings/orders',
    }

    const defaultButtonLabels: Record<string, string> = {
      home: 'Ir al inicio',
      onboarding: 'Comenzar',
      moments: 'Ir a Moments',
      community: 'Ir a mi comunidad',
      membership: 'Ver mi membresía',
      order: 'Ver mi pedido',
      link: 'Continuar',
    }

    const resolvedButtonLabel =
      thankyou?.button ||
      defaultButtonLabels[resolvedAction] ||
      'Continuar'

    const internalDestination =
      internalDestinations[resolvedAction]

    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-12 h-12 text-green-600" />
        </div>

        <h2 className="text-3xl font-bold mb-2">
          {thankyou?.title ||
            (!i18nLoading && t('title_payment_success'))}
        </h2>

        <div className="prose prose-neutral max-w-none text-muted-foreground mb-6 mx-auto">
          <div
            dangerouslySetInnerHTML={{
              __html:
                thankyou?.message ||
                (!i18nLoading
                  ? t('text_payment_success')
                  : ''),
            }}
          />
        </div>

        {thankyou?.video && (
          <div className="aspect-video max-w-xl mx-auto mb-6 rounded-lg overflow-hidden">
            <iframe
              src={toEmbedUrl(thankyou.video)}
              className="w-full h-full"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        )}

        {internalDestination && (
          <CompleteAccountGatedActionButton
            onAuthedAction={() => {
              window.location.href = internalDestination
            }}
          >
            {resolvedButtonLabel}
          </CompleteAccountGatedActionButton>
        )}

        {resolvedAction === 'link' && thankyou?.link && (
          <Button asChild>
            <a
              href={thankyou.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {resolvedButtonLabel}
            </a>
          </Button>
        )}
      </div>
    )
  }

  if (!hasStripe && !hasCrypto) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        {!i18nLoading && t("text_no_payment_methods")}
      </div>
    );
  }

  return (
    <div
      className="
        w-full
        min-h-0
        max-h-[75vh]
        overflow-y-scroll
        space-y-6
        pb-8
        pr-1
        sm:max-h-none
        sm:overflow-visible
        sm:pb-0
        sm:pr-0
      "
      style={{
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div>
        <h2 className="text-xl font-semibold tracking-tight">
          {!i18nLoading && t("heading_payment")}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {!i18nLoading && t("text_complete_purchase")}
        </p>
      </div>

      {bumpOffer && (
        <div className="p-4 border rounded-lg bg-muted/40">
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              checked={bumpSelected}
              onChange={(e) => setBumpSelected(e.target.checked)}
              className="mt-1"
            />
            <div className="flex-1">
              <img
                src={bumpOffer.image}
                alt={bumpOffer.name}
                className="w-16 h-16 object-cover rounded mb-2"
              />
              <h3 className="font-medium">{bumpOffer.name}</h3>
              <p className="text-xs text-muted-foreground">{bumpOffer.description}</p>
              <p className="font-semibold mt-1">${bumpOffer.price.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {shouldShowPaymentSelector && (
        <div
          className={[
            'mb-4 grid gap-2',
            availablePaymentMethods.length === 2
              ? 'grid-cols-2'
              : 'grid-cols-3',
          ].join(' ')}
        >
          {hasStripe && (
            <button
              type="button"
              onClick={() => setMethod('card')}
              className={[
                'min-w-0 rounded-xl border px-2 py-2.5 text-center text-xs transition sm:px-4 sm:py-3 sm:text-left sm:text-sm',
                method === 'card'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background hover:bg-muted',
              ].join(' ')}
            >
              <div className="font-medium">Tarjeta</div>
              <div className="hidden text-xs text-muted-foreground sm:block">Pago normal</div>
            </button>
          )}

          {cryptoAvailable && (
            <button
              type="button"
              onClick={() => setMethod('crypto')}
              className={[
                'min-w-0 rounded-xl border px-2 py-2.5 text-center text-xs transition sm:px-4 sm:py-3 sm:text-left sm:text-sm',
                method === 'crypto'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background hover:bg-muted',
              ].join(' ')}
            >
              <div className="font-medium">Crypto</div>
              <div className="hidden text-xs text-muted-foreground sm:block">
                {isRecurring
                  ? 'Pago manual mensual'
                  : 'Wallet directa'}
              </div>
            </button>
          )}

          {hasTransak && (
            <button
              type="button"
              onClick={() => setMethod('transak')}
              className={[
                'min-w-0 rounded-xl border px-2 py-2.5 text-center text-xs transition sm:px-4 sm:py-3 sm:text-left sm:text-sm',
                method === 'transak'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background hover:bg-muted',
              ].join(' ')}
            >
              <div className="font-medium">Tarjeta vía Transak</div>
              <div className="hidden text-xs text-muted-foreground sm:block">
                Recibe crypto
              </div>
            </button>
          )}
        </div>
      )}

      {method === "card" && (
        <form onSubmit={handleSubmit} className="space-y-6 pb-2">
          {userEmail ? (
            <>
              <input type="hidden" value={userEmail} />
              <p className="text-sm text-muted-foreground">
                {!i18nLoading && t("text_paying_as")}{" "}
                <span className="font-medium">{userEmail}</span>
              </p>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{!i18nLoading && t("input_email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 text-base"
                placeholder="you@email.com"
                required
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">{!i18nLoading && t("input_name_card")}</Label>
            <Input
              value={name || userFullName || ""}
              onChange={(e) => setName(e.target.value)}
              className="h-11 text-base"
              placeholder="Nombre del titular"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">{!i18nLoading && t("input_card_details")}</Label>
              {isMxInstallmentsFlow ? (
                <div className="border rounded-lg p-3">
                  <PaymentElement
                    onChange={(e) => {
                      /*
                      console.log('PAYMENT ELEMENT CHANGE', {
                        complete: e.complete,
                        empty: e.empty,
                        collapsed: (e as any)?.collapsed,
                        valueType: (e as any)?.value?.type,
                      })
                      */
                      setCardComplete(!!e.complete)
                    }}
                  />
                </div>
              ) : (
                <PaymentCard
                  onCompleteChange={(complete) => {
                    setCardComplete(complete)
                  }}
                />
              )}
          </div>

          <div className="border-t pt-6 space-y-3 text-sm">
            {data.recurringPlan && (
              <div className="rounded-md border bg-muted/20 px-3 py-2 text-muted-foreground text-sm">
                {recurringPreview?.label
                ? recurringPreview.label
                : getRecurringCheckoutDescription(data.recurringPlan, currency, formatMoney)}
              </div>
            )}

            {!isRecurring && quantity > 1 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Cantidad</span>
                <span>
                  {quantity} × {formatMoney(Number(unitPrice), data.currency)}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span>{!i18nLoading && t("text_subtotal")}</span>
              <span>{formatMoney(Number(amountToday), data.currency)}</span>
            </div>

            {!isEnergyCheckout && (
            <>
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
              <Label className="text-sm font-medium">Cupón de descuento</Label>

              {appliedCoupon ? (
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">{appliedCoupon.code}</div>
                    <div className="text-xs text-muted-foreground">
                      Descuento: {formatMoney(Number(appliedCoupon.discountAmount || 0), data.currency)}
                      {appliedCoupon.durationType === 'repeating' && appliedCoupon.durationMonths
                        ? ` durante ${appliedCoupon.durationMonths} meses`
                        : ''}
                    </div>
                  </div>

                  <Button type="button" variant="outline" size="sm" onClick={removeCoupon}>
                    Quitar
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase())
                      setCouponError(null)
                    }}
                    placeholder="VIP2999"
                    className="h-10"
                  />

                  <Button
                    type="button"
                    variant="outline"
                    onClick={applyCoupon}
                    disabled={couponLoading}
                  >
                    {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aplicar'}
                  </Button>
                </div>
              )}

              {couponError ? (
                <div className="text-xs text-red-600">{couponError}</div>
              ) : null}
            </div>

            {appliedCoupon && (
              <div className="flex justify-between text-green-700">
                <span>Descuento</span>
                <span>-{formatMoney(Number(appliedCoupon.discountAmount || 0), data.currency)}</span>
              </div>
            )}
            </>
            )}

            {bumpSelected && bumpOffer && (
              <div className="flex justify-between">
                <span>{bumpOffer.name}</span>
                <span>{formatMoney(bumpOffer.price, data.currency)}</span>
              </div>
            )}

            <div className="flex justify-between font-semibold text-lg pt-2">
              <span>{!i18nLoading && t("text_total")}</span>
              <span>{formatMoney(Number(payableAmount), data.currency)}</span>
            </div>
          </div>

          {isAdmin && !adminLoading && (
            <div
              className="
                rounded-lg
                border
                border-amber-500/30
                bg-amber-500/10
                px-3
                py-2
                text-xs
                text-amber-700
                dark:text-amber-300
              "
            >
              Modo ADMIN: este pago será simulado.
              No se creará ninguna orden ni se realizará ningún cargo.
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-12 text-base font-semibold rounded-lg"
            disabled={
              loading ||
              adminLoading ||
              (
                !isAdmin &&
                (
                  !stripe ||
                  !cardComplete
                )
              )
            }
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {!i18nLoading && t("text_processing")}
              </>
            ) : isAdmin ? (
              `Simular pago ${formatMoney(
                Number(payableAmount),
                data.currency
              )} ${data.currency}`
            ) : (
              `Pagar ${formatMoney(
                Number(payableAmount),
                data.currency
              )} ${data.currency}`
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            El cargo se realizará en <strong>{data.currency}</strong>.
          </p>

        </form>
      )}

      {method === "crypto" && (
        <CryptoCheckoutPanel
          amount={String(payableAmountUSD)}
          token={data.paymentMethods.cryptoToken}
          network={data.paymentMethods.cryptoChain}
          productId={data.productId}
          sellerId={data.sellerId}
          userId={data.userId}
          customerEmail={userEmail}
          aiAgentId={data.aiAgentId}
          platformFee={data.platformFee}
          markup={data.markup}
          markupWallet={data.markupWallet}
        />
      )}

      {method === 'transak' && (
        <TransakCheckoutPanel
          amount={String(payableAmountUSD)}
          productId={data.productId}
          sellerId={data.sellerId}
          userId={data.userId}
          customerEmail={userEmail}
          walletAddress={data.paymentMethods?.cryptoWallet || ''}
          chain={data.paymentMethods?.cryptoChain || ''}
          token={data.paymentMethods?.cryptoToken || ''}
        />
      )}
    </div>
  );
}

export function CheckoutFormBlock({ data, isEditing, onChange, onSuccess }: CheckoutFormBlockProps) {
  const [bumpSelected, setBumpSelected] = useState(false)

  const [publishableKey, setPublishableKey] = useState<string | null>(null)
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(null)
  const [directChargeMode, setDirectChargeMode] = useState(false)

  const [stripePlatformPromise, setStripePlatformPromise] = useState<Promise<Stripe | null> | null>(null)
  const [stripeConnectPromise, setStripeConnectPromise] = useState<Promise<Stripe | null> | null>(null)
  const [mxClientSecret, setMxClientSecret] = useState<string | null>(null)

  const { userId } = useCurrentUser()
  const { sellerId } = useSellerId()
  const { funnelOwnerId } = useFunnelOwnerId()

  const {
    isAdmin,
    loading: adminLoading,
  } = useIsAdmin()

  const isFunnel =
    !!data.funnelPageId ||
    !!funnelOwnerId

  const isCommunityAccessRecurring =
    Boolean(
      data.platform?.enabled &&
      data.platform?.kind ===
        'community_access' &&
      data.platform?.access_mode ===
        'recurring'
    )

  const isPlatform =
    Boolean(
      data.platform?.enabled &&
      !isCommunityAccessRecurring
    )

  const payee: 'seller' | 'platform' =
    data.payee
    ?? (
      isPlatform
        ? 'platform'
        : (
            isFunnel ||
            data.sellerId != null ||
            isCommunityAccessRecurring
          )
          ? 'seller'
          : 'platform'
    )

  // ✅ Solo necesitamos ownerAccountId si scope === 'seller'
  const ownerAccountId =
    payee === 'seller'
      ? (
          data.sellerId ??
          data.platform?.seller_id ??
          funnelOwnerId ??
          sellerId ??
          userId
        )
      : null

  const isRecurringForMx = !!data.recurringPlan

  const quantityForMx = isRecurringForMx
    ? 1
    : Math.max(1, Math.min(99, Math.floor(Number(data.quantity || 1))))

  const unitPriceForMx = Number.isFinite(Number(data.unitPrice))
    ? Number(data.unitPrice)
    : Number(data.amount || 99.99)

  const amountForMx = Number.isFinite(Number(data.amount))
    ? Number(data.amount)
    : unitPriceForMx * quantityForMx

  useEffect(() => {
    if (payee === 'seller' && !ownerAccountId) return

    // reset para evitar mezclas
    setPublishableKey(null)
    setConnectedAccountId(null)
    setStripePlatformPromise(null)
    setStripeConnectPromise(null)
    setDirectChargeMode(false)

    const tenantSellerId =
      (
        data.sellerId ??
        data.platform?.seller_id ??
        funnelOwnerId ??
        sellerId ??
        null
      )

    const url =
      payee === 'platform'
        ? `/api/stripe/publishable-key?scope=platform&identifier=${tenantSellerId ?? ''}&user_id=${userId}`
        : `/api/stripe/publishable-key?scope=seller&seller_id=${ownerAccountId}`

    fetch(url, { cache: 'no-store' })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error(j?.error || 'Error fetching publishable key')

        const pk = j?.publishableKey
        const ca = j?.connectedAccountId || null
        const directCharge = !!j?.directCharge

        if (!pk) throw new Error('No publishableKey')

        setPublishableKey(pk)
        setConnectedAccountId(ca)
        setDirectChargeMode(directCharge)

        // ✅ plataforma siempre disponible
        setStripePlatformPromise(loadStripe(pk))

        // ✅ connect SOLO si scope=seller y hay cuenta conectada
        if (payee === 'seller' && ca) {
          setStripeConnectPromise(loadStripe(pk, { stripeAccount: ca }))
        } else {
          setStripeConnectPromise(null)
        }
      })
      .catch((err) => {
        console.error('Error loading Stripe keys:', err)
      })
  }, [payee, ownerAccountId])

  useEffect(() => {
    const shouldPrepareMxIntent =
      !adminLoading &&
      !isAdmin &&
      data.currency === 'MXN' &&
      payee === 'seller' &&
      directChargeMode === true &&
      !!ownerAccountId

    if (!shouldPrepareMxIntent) {
      setMxClientSecret(null)
      return
    }

    const cacheKey = getMxCheckoutKey({
      sellerId: ownerAccountId,
      productId: data.productId || 0,
      funnelPageId: data.funnelPageId || 0,
      currency: data.currency,
      amount: amountForMx,
      quantity: quantityForMx,
      platform: data.platform,
    })

    let cancelled = false

    async function run() {
      let forceNewIntent = false

      try {
        const cachedRaw = sessionStorage.getItem(cacheKey)
        const cached = cachedRaw ? JSON.parse(cachedRaw) : null

        if (cached?.clientSecret && cached?.paymentIntentId) {
          try {
            const statusRes = await fetch('/api/stripe/payment-intent-status', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              cache: 'no-store',
              body: JSON.stringify({
                payment_intent_id: cached.paymentIntentId,
                seller_id: ownerAccountId,
                user_id: userId,
              }),
            })

            const statusJson = await statusRes.json()

            /*
            console.log('♻️ Cached MX PaymentIntent status', {
              cached,
              statusJson,
            })
            */

            if (statusRes.ok && statusJson?.reusable) {
              if (!cancelled) {
                setMxClientSecret(cached.clientSecret)
              }
              return
            }

            console.warn('🧹 Cached MX PaymentIntent is not reusable. Removing cache.', {
              paymentIntentId: cached.paymentIntentId,
              status: statusJson?.status,
              error: statusJson?.error,
            })
            
            sessionStorage.removeItem(cacheKey)
            forceNewIntent = true
          } catch (statusError) {
            console.error('Error checking cached MX PaymentIntent status:', statusError)

            // Si no se puede verificar, mejor no uses un intent posiblemente muerto.
            sessionStorage.removeItem(cacheKey)
            forceNewIntent = true
          }
        }

        const res = await fetch('/api/stripe/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: amountForMx,
            currency: data.currency,
            productName: data.productName || 'Producto',
            productDescription: data.productDescription || '',
            email: '',
            buyer_name: '',
            seller_id: ownerAccountId,
            user_id: userId,
            product_id: data.productId || 0,
            ai_agent_id: data.aiAgentId,
            funnel_page_id: data.funnelPageId || 0,
            idempotency_key: forceNewIntent
              ? `${cacheKey}_retry_${Date.now()}`
              : cacheKey,
            quantity: quantityForMx,
            unit_price: unitPriceForMx,
            platform: data.platform?.enabled
              ? {
                  enabled: true,
                  kind: data.platform.kind,
                  identifier: (data.sellerId ?? funnelOwnerId ?? sellerId ?? null),
                  ...(data.platform.kind === 'credits'
                    ? { pack_key: data.platform.pack_key }
                    : {}),
                  ...(data.platform.kind === 'storage'
                    ? { plan_key: data.platform.plan_key }
                    : {}),
                  ...(data.platform.kind === 'membership'
                    ? { group_id: data.platform.group_id }
                    : {}),
                }
              : undefined,
          }),
        })

        const j = await res.json()
        if (!res.ok) throw new Error(j?.error || 'Error creating MX intent')

        if (!cancelled) {
          setMxClientSecret(j.clientSecret || null)

          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              clientSecret: j.clientSecret,
              paymentIntentId: j.payment_intent_id,
              createdAt: Date.now(),
            })
          )

          /*
          console.log('MX PRECREATE CLIENT SECRET SET', {
            hasClientSecret: !!j.clientSecret,
            paymentIntentId: j.payment_intent_id,
            directCharge: j.directCharge,
            stripeAccount: j.stripeAccount,
          })
          */
        }
      } catch (e) {
        console.error('Error preparing MX PaymentIntent:', e)
        if (!cancelled) setMxClientSecret(null)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    amountForMx,
    quantityForMx,
    unitPriceForMx,
    data.currency,
    data.productId,
    data.aiAgentId,
    data.funnelPageId,
    data.productName,
    data.productDescription,
    data.platform?.enabled,
    data.platform?.kind,
    data.platform?.pack_key,
    data.platform?.plan_key,
    data.platform?.group_id,
    payee,
    directChargeMode,
    ownerAccountId,
    userId,
    funnelOwnerId,
    sellerId,
  ])

  // -------------------------
  // Editor mode
  // -------------------------
  if (isEditing) {
    return (
      <div className="space-y-4">
        <StripeConnectButton />
        {/* ...lo demás igual... */}
      </div>
    )
  }

  // -------------------------
  // Recurring / Stripe instance selection
  // -------------------------
  const needsRecurring = !!data.recurringPlan

  const allowRecurringCrypto =
    Boolean(
      needsRecurring &&
      data.allowManualCryptoForRecurring &&
      data.paymentMethods?.hasCrypto
    )

  const sellerRecurringWithoutStripe =
    Boolean(
      needsRecurring &&
      payee === 'seller' &&
      !stripeConnectPromise
    )

  if (
    sellerRecurringWithoutStripe &&
    !allowRecurringCrypto
  ) {
    return (
      <div>
        Este creador aún no tiene Stripe Connect configurado para suscripciones.
      </div>
    )
  }

  // ✅ Si recurring:
  // - seller => connect promise
  // - platform => platform promise
  const needsConnectElements =
    payee === 'seller' &&
    (
      (
        needsRecurring &&
        !sellerRecurringWithoutStripe
      ) ||
      directChargeMode
    )

  const stripePromiseToUse =
    needsConnectElements
      ? stripeConnectPromise
      : stripePlatformPromise

  if (!stripePromiseToUse) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <p>Inicializando checkout seguro...</p>
      </div>
    )
  }

  // ✅ elementsKey estable por scope
  const elementsKey = needsConnectElements
    ? `connect-${ownerAccountId}-${connectedAccountId || 'none'}-${directChargeMode ? 'direct' : 'recurring'}-${mxClientSecret || 'no-secret'}`
    : `platform-${publishableKey || 'none'}`

  const isMxInstallmentsCheckout =
    !isAdmin &&
    data.currency === 'MXN' &&
    payee === 'seller' &&
    directChargeMode === true

  const elementsOptions = isMxInstallmentsCheckout && mxClientSecret
    ? { clientSecret: mxClientSecret }
    : undefined

  if (isMxInstallmentsCheckout && !mxClientSecret) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mb-2" />
        <p>Preparando opciones de pago...</p>
      </div>
    )
  }

  /*
  console.log('MX ELEMENTS DEBUG', {
    currency: data.currency,
    enableInstallments: data.enableInstallments,
    payee,
    directChargeMode,
    isMxInstallmentsCheckout,
    connectedAccountId,
    hasMxClientSecret: !!mxClientSecret,
    elementsOptions,
    elementsKey,
  })
  */

  return (
    <Elements stripe={stripePromiseToUse} options={elementsOptions} key={elementsKey}>
      <CheckoutFormInner
        data={{
          ...data,

          paymentMethods: {
            ...data.paymentMethods,

            /*
            * Si es una suscripción del seller pero
            * no tiene Stripe Connect, no mostramos
            * tarjeta. Crypto manual sigue disponible.
            */
            hasStripe:
              sellerRecurringWithoutStripe
                ? false
                : Boolean(
                    data.paymentMethods
                      ?.hasStripe
                  ),
          },

          sellerId:
            payee === 'seller'
              ? ownerAccountId
              : data.sellerId,

          userId,

          payee,

          directChargeMode,

          isMxInstallmentsCheckout,

          onSuccess,
        }}
        bumpSelected={bumpSelected}
        setBumpSelected={setBumpSelected}
      />
    </Elements>
  )
}

